import CheckIcon from "@mui/icons-material/CheckRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { Button } from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import { arraysHaveSameItems } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../api/adminApi";
import type { ChecklistTask, ChecklistTaskStatus } from "../../../api/types/objectTypes";
import useUserContext from "../../../context/useUserContext";
import BulkApprovalDialog from "../../approvals/BulkApprovalDialog";
import ChecklistTaskStatusTag from "./ChecklistTaskStatusTag";
import ObjectTasksContext from "./ObjectTasksContext";
import { getAvailableBulkStatuses, isTaskApprovalAllowed } from "./objectTasksHelper";

const changeChecklistTaskStatuses = withErrorHandling(adminApi.changeChecklistTaskStatuses);

interface Props {
  allTasks: ChecklistTask[];
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}

const ObjectTasksGridToolbar = ({ allTasks, selectedIds, setSelectedIds }: Props) => {
  const { objectType, objectId, onChecklistTasksUpdate, onApprovalActionExecuted, hasPermissionsToManageObjectData } =
    useDefinedContext(ObjectTasksContext, "ObjectTasksContext");
  const { sendNotificationError } = useNotificationContext();
  const { id: userId } = useUserContext();
  const [saving, setSaving] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);

  const selectedTasks = useMemo(() => allTasks.filter((t) => selectedIds.includes(t.id)), [allTasks, selectedIds]);

  const availableStatuses = useMemo(() => getAvailableBulkStatuses(selectedTasks), [selectedTasks]);

  const approvableTasks = useMemo(
    () => selectedTasks.filter((t) => isTaskApprovalAllowed(t, userId)),
    [selectedTasks, userId]
  );

  const isApproveEnabled = useMemo(
    () =>
      arraysHaveSameItems(
        approvableTasks.map((t) => t.id),
        selectedTasks.map((t) => t.id)
      ),
    [approvableTasks, selectedTasks]
  );

  const handleStatusChange = async (status: ChecklistTaskStatus) => {
    setSaving(true);

    const [data, error] = await changeChecklistTaskStatuses(objectType, objectId, {
      tasks: selectedTasks.map((task) => ({ taskId: task.id, status })),
    });

    setSaving(false);

    if (error) {
      logError(error, "[ObjectTasksGridToolbar] handleStatusChange");
      sendNotificationError(error.message);
      return;
    }

    onChecklistTasksUpdate(data);
    setSelectedIds([]);
  };

  const handleBulkApproveSuccess = () => {
    onApprovalActionExecuted();
    setSelectedIds([]);
  };

  return (
    <>
      {hasPermissionsToManageObjectData && (
        <ActionsMenuButton
          text="Change Status"
          color="secondary"
          icon={<TaskAltRoundedIcon />}
          disabled={availableStatuses.length === 0 || saving}
          loading={saving}
          items={availableStatuses.map((status) => ({
            label: <ChecklistTaskStatusTag status={status} />,
            onClick: () => handleStatusChange(status),
          }))}
        />
      )}
      {approvableTasks.length > 0 && (
        <Button
          variant="text"
          color="primary"
          startIcon={<CheckIcon />}
          disabled={!isApproveEnabled || saving}
          onClick={() => setShowApprovalDialog(true)}
        >
          Approve
        </Button>
      )}
      {showApprovalDialog && (
        <BulkApprovalDialog
          approvalIds={approvableTasks.map((t) => t.approvalId!)}
          prompt={`Approve ${approvableTasks.length} selected task(s)?`}
          onClose={() => setShowApprovalDialog(false)}
          onApply={handleBulkApproveSuccess}
        />
      )}
    </>
  );
};

export default ObjectTasksGridToolbar;
